import { useState, useCallback, useRef } from 'react';
import { apiService } from '../services/api';

interface ProgressState {
  isVisible: boolean;
  percentage: number;
  message: string;
  title: string;
}

interface WorkspaceReadyResult {
  success: boolean;
  error?: string;
  orgId?: string;
	workspaceId?: string;
  containerAppUrl?: string | null;
  isReady?: boolean; // Whether workspace is ready for use
}

interface WorkspaceReadinessStatus {
  isReady: boolean;
  infraReady: boolean;
	snapshotReady: boolean;
	applyReady: boolean;
	progress: number;
	hasError: boolean;
  message?: string;
}

export const useWorkspaceManagement = () => {
  const [progress, setProgress] = useState<ProgressState>({
    isVisible: false,
    percentage: 0,
    message: '',
    title: 'Preparing Workspace'
  });
  const [isReady, setIsReady] = useState(false);
  const isSnapshotInFlight = useRef(false);

  const updateProgress = useCallback((percentage: number, message: string, title?: string) => {
    console.log('updateProgress', percentage, message, title);
    setProgress(prev => ({
      isVisible: true,
      percentage,
      message,
      title: title ?? prev.title
    }));
  }, []);

  const hideProgress = useCallback(() => {
    console.log('hideProgress');
    setProgress({
      isVisible: false,
      percentage: 0,
      message: '',
      title: 'Preparing Workspace'
    });
  }, []);

  const showError = useCallback((message: string) => {
    console.error('Workspace initialization error:', message);
  }, []);
	
	const buildWorkspaceReadiness = useCallback((workspace: {
		infra_status: 'pending' | 'running' | 'completed' | 'failed';
		infra_error_info?: string | null;
		snapshot_status?: 'pending' | 'running' | 'completed' | 'failed' | null;
		snapshot_progress?: number | null;
		snapshot_message?: string | null;
		snapshot_error_info?: string | null;
		snapshot_apply_status?: 'pending' | 'running' | 'completed' | 'failed' | null;
		snapshot_apply_progress?: number | null;
		snapshot_apply_message?: string | null;
	}, options?: { includeInfra?: boolean }): WorkspaceReadinessStatus => {
		const includeInfra = options?.includeInfra ?? true;
		const infraReady = workspace.infra_status === 'completed';
		const snapshotStatus = workspace.snapshot_status ?? 'pending';
		const applyStatus = workspace.snapshot_apply_status ?? 'pending';
		const snapshotReady = snapshotStatus === 'completed';
		const applyReady = applyStatus === 'completed';
		const snapshotProgress = typeof workspace.snapshot_progress === 'number' ? workspace.snapshot_progress : 0;
		const applyProgress = typeof workspace.snapshot_apply_progress === 'number' ? workspace.snapshot_apply_progress : 0;
		
		if (includeInfra && !infraReady) {
			const message = workspace.infra_status === 'failed'
				? workspace.infra_error_info || 'Workspace provisioning failed'
				: `Workspace provisioning ${workspace.infra_status}`;
			const hasError = workspace.infra_status === 'failed';
			return {
				isReady: false,
				infraReady: false,
				snapshotReady: false,
				applyReady: false,
				progress: 0,
				hasError,
				message,
			};
		}
		
		if (!snapshotReady) {
			const message = snapshotStatus === 'failed'
				? workspace.snapshot_error_info || workspace.snapshot_message || 'Metadata snapshot failed'
				: workspace.snapshot_message || (snapshotStatus === 'pending' ? 'Snapshot queued' : 'Creating metadata snapshot...');
			const hasError = snapshotStatus === 'failed';
			const progress = includeInfra
				? 20 + Math.round(snapshotProgress * 0.75) //75% if infra is included
				: Math.round(snapshotProgress * 0.9) //90% if infra is not included
			return {
				isReady: false,
				infraReady: true,
				snapshotReady: false,
				applyReady: false,
				progress,
				hasError,
				message,
			};
		}
		
		if (!applyReady) {
			const message = applyStatus === 'failed'
				? workspace.snapshot_apply_message || 'Applying snapshot failed'
				: workspace.snapshot_apply_message || 'Applying snapshot to workspace...';
			const hasError = applyStatus === 'failed';
			const progress = includeInfra
				? 95 + Math.round(applyProgress * 0.05) //5% if infra is included
				: 90 + Math.round(applyProgress * 0.1); //10% if infra is not included
			return {
				isReady: false,
				infraReady: true,
				snapshotReady: true,
				applyReady: false,
				progress,
				hasError,
				message,
			};
		}
		
		return {
			isReady: true,
			infraReady: true,
			snapshotReady: true,
			applyReady: true,
			progress: 100,
			hasError: false,
			message: 'Workspace ready',
		};
	}, []);

	/**
	 * Check if workspace is ready (infra completed + metadata exists).
	 * Returns readiness status without triggering any operations.
	 */
	const checkWorkspaceReadiness = useCallback(async (
		workspaceId: string,
		options?: { includeInfra?: boolean }
	): Promise<WorkspaceReadinessStatus> => {
		try {
			const workspaceResponse = await apiService.getWorkspace(workspaceId);
			
			if (!workspaceResponse.success || !workspaceResponse.data) {
				return {
					isReady: false,
					infraReady: false,
					snapshotReady: false,
					applyReady: false,
					progress: 0,
					hasError: true,
					message: workspaceResponse.error || 'Failed to load workspace information'
				};
			}

			const workspace = workspaceResponse.data;
			const readiness = buildWorkspaceReadiness(workspace, options);
			return readiness;
		} catch (ex) {
			console.error('Error checking workspace readiness:', ex);
			return {
				isReady: false,
				infraReady: false,
				snapshotReady: false,
				applyReady: false,
				progress: 0,
				hasError: true,
				message: 'Error checking workspace status'
			};
		}
	}, [buildWorkspaceReadiness]);

	/**
	 * Poll for workspace readiness until ready or timeout.
	 * Used when workspace is not ready and we need to wait for background processes.
	 */
	const pollWorkspaceReadiness = useCallback(async (
		workspaceId: string,
		title?: string,
		timeoutInMin: number = 30,
		options?: { includeInfra?: boolean }
	): Promise<WorkspaceReadinessStatus> => {
		const start = Date.now();
		const timeout = timeoutInMin * 60 * 1000;
		const delayInSec = 3; // Check every 3 seconds

		try {
			while (Date.now() - start < timeout) {
				const status = await checkWorkspaceReadiness(workspaceId, options);
				
				if (status.isReady) {
					updateProgress(100, 'Workspace ready', title);
					return status;
				}
				
				if (status.hasError) {
					updateProgress(100, status.message || 'Workspace setup failed', title);
					return status;
				}

				updateProgress(status.progress, status.message || 'Preparing workspace...', title);

				await new Promise((r) => setTimeout(r, delayInSec * 1000));
			}

			// Timeout
			const finalStatus = await checkWorkspaceReadiness(workspaceId, options);
			updateProgress(100, 'Workspace readiness check timed out', title);
			return finalStatus;
		} catch (ex) {
			console.error('Error polling workspace readiness:', ex);
			updateProgress(100, 'Error checking workspace status', title);
			return {
				isReady: false,
				infraReady: false,
				snapshotReady: false,
				applyReady: false,
				progress: 0,
				hasError: true,
				message: 'Error checking workspace status'
			};
		}
	}, [checkWorkspaceReadiness, updateProgress]);

	/**
	 * Shared function to execute metadata snapshot creation.
	 * Starts snapshot and returns the instance id for logging.
	 */
	const executeMetadataSnapshot = useCallback(async (
		workspaceId?: string,
		title?: string
	): Promise<{ success: boolean; error?: string }> => {
		// Reset progress to 0% when starting snapshot
		updateProgress(0, 'Starting metadata snapshot...', title);
		
		const snapshotResponse = await apiService.startMetadataSnapshot(workspaceId);

		if (!snapshotResponse.success || !snapshotResponse.data) {
			const error = snapshotResponse.error || 'Failed to start metadata snapshot';
			updateProgress(100, `Error: ${error}`, title);
			return { success: false, error };
		}

		return { success: true };
	}, [updateProgress]);

  /**
   * Wait for workspace to be ready by checking readiness and polling if needed.
   * Does NOT trigger metadata snapshots - only monitors status.
   * Returns workspace details once ready (or after timeout).
   */
	const waitForWorkspaceReady = useCallback(async (workspaceId: string): Promise<WorkspaceReadyResult> => {
		setIsReady(false);
		try {
			// Step 1: Poll until ready (or timeout)
			updateProgress(0, 'Checking workspace status...', 'Preparing Workspace');
			const readinessStatus = await pollWorkspaceReadiness(
				workspaceId,
				'Preparing Workspace',
				undefined,
				{ includeInfra: true }
			);

			// Step 2: Return result based on final readiness status
      if (readinessStatus.isReady) {
        updateProgress(100, 'Workspace ready', 'Preparing Workspace');
				const workspaceResponse = await apiService.getWorkspace(workspaceId);
				if (!workspaceResponse.success || !workspaceResponse.data) {
					const error = workspaceResponse.error || 'Failed to load workspace';
					showError(error);
					setIsReady(false);
					return { success: false, error, isReady: false };
				}
				const workspace = workspaceResponse.data;
				const orgId = workspace.org_id;
				const containerAppUrl = workspace.container_app_url || null;
        setIsReady(true);
        return { 
          success: true, 
          orgId, 
          workspaceId: workspaceId || undefined, 
          containerAppUrl,
          isReady: true
        };
      } else {
        // Workspace is not ready - return error but include workspace info
        const error = readinessStatus.message || 'Workspace is not ready';
        updateProgress(100, error, 'Initializing Workspace');
        setIsReady(false);
        return { 
          success: false, 
          error, 
					workspaceId: workspaceId || undefined,
          isReady: false
        };
      }
      
    } catch (ex) {
      console.error('Failed to wait for workspace ready:', ex);
      const error = 'Failed to prepare workspace. Please try again.';
      showError(error);
      setIsReady(false);
      return { success: false, error, isReady: false };
    }
	}, [updateProgress, showError, pollWorkspaceReadiness]);

  /**
   * User-initiated metadata refresh: start a new snapshot and wait for completion.
   * This prevents user interaction during the snapshot process.
   */
	const refreshMetadata = useCallback(async (workspaceId?: string, connectionId?: string): Promise<{ success: boolean; error?: string }> => {
    if (!workspaceId) {
      return { success: false, error: 'Workspace ID is required' };
    }

    // Prevent concurrent refresh requests (guards against rapid double-clicks)
    if (isSnapshotInFlight.current) {
      return { success: false, error: 'A metadata refresh is already in progress.' };
    }
    isSnapshotInFlight.current = true;

    setIsReady(false);
    try {
      // Set title for refresh operation
      updateProgress(0, 'Starting metadata refresh...', 'Refreshing Metadata');
      
      // Start snapshot and wait for completion
			const snapshotResult = await executeMetadataSnapshot(workspaceId, 'Refreshing Metadata');

      if (snapshotResult.success) {
				updateProgress(0, 'Snapshot started, waiting for progress...', 'Refreshing Metadata');
        const readinessStatus = await pollWorkspaceReadiness(
					workspaceId,
					'Refreshing Metadata',
					undefined,
					{ includeInfra: false }
				);
        
        if (readinessStatus.isReady) {
          updateProgress(100, 'Metadata refresh completed', 'Refreshing Metadata');
          setIsReady(true);
          setTimeout(() => {
            hideProgress();
          }, 2000);
          return { success: true };
        }
        
        updateProgress(100, readinessStatus.message || 'Metadata refresh incomplete', 'Refreshing Metadata');
        setIsReady(false);
        return { success: false, error: readinessStatus.message || 'Metadata refresh incomplete' };
      } else {
        setIsReady(false);
        return snapshotResult;
      }
    } catch (ex) {
      console.error('Metadata refresh failed:', ex);
      const error = 'Failed to refresh metadata. Please try again.';
      updateProgress(100, error, 'Refreshing Metadata');
      setIsReady(false);
      return { success: false, error };
    } finally {
      isSnapshotInFlight.current = false;
    }
	}, [updateProgress, hideProgress, executeMetadataSnapshot, pollWorkspaceReadiness]);

  return {
    waitForWorkspaceReady,
    refreshMetadata,
    progress,
    isReady,
    setIsReady,
    updateProgress,
    hideProgress
  };
};
